// V4.14 — "Analyse teilen": snapshots the last AI answer + the current
// canvas into a public read-only link (server-side, whitelisted).
// Native share sheet on touch devices, fallback dialog on desktop.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Copy, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../api/client";
import { canUseNativeShare } from "../../lib/shareCapability";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AnalysisShareButton = () => {
  const { t } = useTranslation("share");
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareText, setShareText] = useState("");
  const [shareUrl, setShareUrl] = useState("");

  const handleShare = async () => {
    if (busy) return;
    let data;
    try {
      setBusy(true);
      const r = await apiClient.post("/share/analysis", {}, { timeout: 25000 });
      data = r.data;
    } catch (err) {
      if (err?.response?.status === 422) toast.error(t("analysis.notReady"));
      else toast.error(t("analysis.shareError"));
      return;
    } finally {
      setBusy(false);
    }
    const url = `${BACKEND_URL}/api/d/${data.share_id}`;
    const text = [
      `🤖 ${t("analysis.found")}`,
      "",
      data.title,
      "",
      `${t("analysis.viewAnalysis")}:`,
      url,
    ].join("\n");
    setShareText(text);
    setShareUrl(url);
    apiClient
      .post(`/share/public/${data.share_id}/event`, { type: "sheet_opened" })
      .catch(() => {});
    if (canUseNativeShare()) {
      try {
        await navigator.share({ title: t("analysis.found"), text });
      } catch (err) {
        if (err?.name !== "AbortError" && err?.name !== "InvalidStateError") {
          setDialogOpen(true);
        }
      }
    } else {
      setDialogOpen(true);
    }
  };

  const encoded = encodeURIComponent(shareText);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success(t("copied"));
    } catch {
      toast.error(t("analysis.shareError"));
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="ai-share-analysis-btn"
        onClick={handleShare}
        disabled={busy}
        title={t("analysis.shareBtn")}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300 disabled:opacity-50"
      >
        <Share2 className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("analysis.shareBtn")}</span>
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="analysis-share-dialog"
          aria-describedby={undefined}
          className="max-w-sm bg-zinc-950 border-zinc-800"
        >
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t("shareVia")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <a
              data-testid="analysis-share-whatsapp"
              href={`https://wa.me/?text=${encoded}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-emerald-500/50"
            >
              <MessageCircle className="h-4 w-4 text-emerald-400" />
              {t("whatsapp")}
            </a>
            <a
              data-testid="analysis-share-telegram"
              href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encoded}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-sky-500/50"
            >
              <Send className="h-4 w-4 text-sky-400" />
              {t("telegram")}
            </a>
            <a
              data-testid="analysis-share-email"
              href={`mailto:?subject=${encodeURIComponent(t("analysis.emailSubject"))}&body=${encoded}`}
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-amber-500/50"
            >
              <Mail className="h-4 w-4 text-amber-400" />
              {t("email")}
            </a>
            <button
              type="button"
              data-testid="analysis-share-copy-btn"
              onClick={copyLink}
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-zinc-500"
            >
              <Copy className="h-4 w-4 text-zinc-400" />
              {t("copyLink")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
